import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";
import { mergeCooperationOfferFieldErrors } from "../../realty/lib/cooperationOffer";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import {
  CREATE_DRAFT_ASYNC_FALLBACK,
  CREATE_PUBLISH_ASYNC_FALLBACK,
  CREATE_SAVE_ASYNC_FALLBACK,
  mapCreateAsyncError,
} from "../lib/createAsyncFeedback";

import {
  addPropertyFeature,
  createProperty,
  deletePropertyImage,
  fetchFeatureCatalog,
  fetchPropertyDetail,
  patchPropertyCore,
  patchPropertyImage,
  publishProperty,
  putPropertyCommercial,
  putPropertyLand,
  putPropertyLocation,
  putPropertyResidential,
  removePropertyFeature,
  reorderPropertyImages,
  uploadPropertyImages,
  propertyImageUploadErrorMessage,
} from "../api/realty";

import {
  DRAFT_QUERY_PARAM,
  SUBTYPE_BY_PROPERTY_TYPE,
  TOTAL_WIZARD_STEPS,
  WIZARD_STEPS,
  buildCommercialPayload,
  buildCorePayload,
  buildDraftBootstrapPayload,
  buildInitialForm,
  buildLandPayload,
  buildLocationPayload,
  buildResidentialPayload,
  collectSpecsFieldIssues,
  deriveWizardStepStates,
  featureIdsFromProperty,
  formFromPropertyDetail,
  isFilled,
  locationReady,
  markStepVisited,
  parseDraftIdParam,
  resolveResumeStep,
  subtypeReady,
  validateStep1ForDraft,
  visitedStepsFromResume,
} from "../lib/wizardContract";

import {
  MEDIA_KIND_PHOTO,
  MEDIA_KIND_PLAN,
  MAIN_ASSIGNMENT_STATUS,
  assertCreateMediaPreflight,
  createMainAssignmentIntent,
  hasBlockingPrepare,
  hasBlockingMediaAfterDrain,
  hasPendingMainAssignmentBlocker,
  hasUploadablePending,
  isMediaWizardRetentionError,
  MediaWizardRetentionError,
  resolvePendingMainAfterRemoval,
  serverMainId,
  splitServerImages,
} from "../lib/wizardMedia";
import usePropertyMediaQueue from "../../../shared/propertyMediaQueue/usePropertyMediaQueue";
import {
  REORDER_ERROR_MESSAGE,
  sortPropertyImages,
} from "../../../shared/propertyMediaManager/propertyMediaOrder";
import { usePropertyMediaReorder } from "../../../shared/propertyMediaManager/usePropertyMediaReorder";
import { usePropertyMediaUploadScheduler } from "../../../shared/propertyMediaManager/usePropertyMediaUploadScheduler";

import CreateStepper from "./CreateStepper";
import CreateNav from "./CreateNav";
import CreateStepType from "./CreateStepType";
import CreateStepLocation from "./CreateStepLocation";
import CreateStepSpecs from "./CreateStepSpecs";
import CreateStepFeatures from "./CreateStepFeatures";
import CreateStepMedia from "./CreateStepMedia";
import CreateStepDescription from "./CreateStepDescription";

import {
  buildLocationApiPayload,
  locationCanonicalReady,
  validateLocationCanonical,
  validateLocationCoordinates,
  validateLocationStructured,
} from "../../geo/lib/locationGeo";
import LandParcelsEditor from "../../landParcels/widgets/LandParcelsEditor";

const CORE_ERROR_KEYS = [
  "title",
  "description_public",
  "description_private",
  "property_type",
  "deal_type",
  "price",
  "currency",
  "status",
  "canonical_region",
  "canonical_settlement",
  "local_area",
  "address_object_id",
  "manual_address_object",
  "building_number",
  "legacy_address",
  "residential_complex",
  "cooperation_terms",
];

const dismissFieldErrorsForKeys = (setFieldErrors, keys) => {
  if (!keys || !keys.length) return;
  setFieldErrors((prev) => {
    let changed = false;
    const next = { ...prev };
    for (const key of keys) {
      if (next[key] !== undefined) {
        delete next[key];
        changed = true;
      }
    }
    return changed ? next : prev;
  });
};

const changedKeys = (prevObj, nextObj) => {
  const keys = new Set([
    ...Object.keys(prevObj || {}),
    ...Object.keys(nextObj || {}),
  ]);
  const out = [];
  for (const key of keys) {
    if ((prevObj || {})[key] !== (nextObj || {})[key]) out.push(key);
  }
  return out;
};

const collectFieldIssues = (form) => {
  const issues = {};
  const num = (v) =>
    v === "" || v === null || v === undefined ? null : Number(v);
  const isBad = (n) => n !== null && Number.isFinite(n) && n <= 0;

  if (isBad(num(form.core.price))) {
    issues.price = "Ціна має бути більшою за 0.";
  }

  Object.assign(issues, collectSpecsFieldIssues(form));

  if (form.location?.canonicalSelectionDirty || form.location?.selectedRegion) {
    Object.assign(issues, validateLocationCanonical(form.location));
  }
  Object.assign(issues, validateLocationStructured(form.location));
  Object.assign(issues, validateLocationCoordinates(form.location));

  return issues;
};

const stepForFieldIssues = (issues) => {
  if (issues.price || issues.title) return 1;
  if (
    issues.canonical_region
    || issues.canonical_settlement
    || issues.local_area
    || issues.address_object_id
    || issues.manual_address_object
    || issues.building_number
    || issues.legacy_address
    || issues.latitude
    || issues.longitude
  ) return 2;
  if (
    issues.total_area_m2 ||
    issues.area_m2 ||
    issues.rooms ||
    issues.total_floors ||
    issues.floor ||
    issues.living_area_m2 ||
    issues.kitchen_area_m2
  ) {
    return 3;
  }
  return null;
};

const stepForCoreErrors = (fieldErrors) => {
  if (
    fieldErrors.title ||
    fieldErrors.price ||
    fieldErrors.currency ||
    fieldErrors.property_type ||
    fieldErrors.deal_type
  ) {
    return 1;
  }
  if (fieldErrors.description_public || fieldErrors.description_private) {
    return 6;
  }
  return null;
};

function CreateRealtyScreenContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const userInfo = useSelector(
    (state) => state.userLogin && state.userLogin.userInfo,
  );

  useEffect(() => {
    if (!userInfo) {
      navigate("/login", { replace: true });
    }
  }, [userInfo, navigate]);

  const draftParam = searchParams.get(DRAFT_QUERY_PARAM);
  const parsedDraftParam = parseDraftIdParam(draftParam);

  const [step, setStep] = useState(1);
  const [visitedSteps, setVisitedSteps] = useState(() => new Set([1]));
  const [step4Acknowledged, setStep4Acknowledged] = useState(false);
  const [step5Acknowledged, setStep5Acknowledged] = useState(false);
  const [form, setForm] = useState(buildInitialForm);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState(() => new Set());
  const [savedFeatureIds, setSavedFeatureIds] = useState(() => new Set());

  const [draftId, setDraftId] = useState(null);
  const [landParcels, setLandParcels] = useState([]);
  const draftCreatePromiseRef = useRef(null);
  const hydratedDraftParamRef = useRef(null);

  const [serverPhotos, setServerPhotos] = useState([]);
  const [serverPlans, setServerPlans] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  /** Pending photo localId explicitly chosen as main before upload (photos only). */
  const [pendingMainLocalId, setPendingMainLocalId] = useState(null);
  const [pendingMainServerAssignment, setPendingMainServerAssignment] =
    useState(null);
  const pendingMainServerAssignmentRef = useRef(null);
  pendingMainServerAssignmentRef.current = pendingMainServerAssignment;
  const [editingItem, setEditingItem] = useState(null);
  const pendingItemsRef = useRef([]);
  const [mediaUploadBusy, setMediaUploadBusy] = useState(false);
  const [photosReorderError, setPhotosReorderError] = useState(null);
  const [plansReorderError, setPlansReorderError] = useState(null);
  const draftIdRef = useRef(null);
  const pendingMainLocalIdRef = useRef(null);
  const uploadPropertyIdRef = useRef(null);
  const canonicalMediaRef = useRef({ image: [], plan: [] });

  pendingItemsRef.current = pendingItems;
  draftIdRef.current = draftId;
  pendingMainLocalIdRef.current = pendingMainLocalId;

  const mediaQueue = usePropertyMediaQueue({
    items: pendingItems,
    setItems: setPendingItems,
    enabled: true,
  });
  const {
    addFiles: queueAddFiles,
    removeItem: queueRemoveItem,
    applyEditedFile: queueApplyEditedFile,
    clearQueue,
    retryPrepare: queueRetryPrepare,
  } = mediaQueue;

  const pendingPhotos = useMemo(
    () => mediaQueue.photos,
    [mediaQueue.photos],
  );
  const pendingPlans = useMemo(
    () => mediaQueue.plans,
    [mediaQueue.plans],
  );

  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [catalogState, setCatalogState] = useState("loading");
  const [catalogError, setCatalogError] = useState(null);

  const [loadState, setLoadState] = useState(
    () => (parsedDraftParam ? "loading" : "ready"),
  );
  const [loadError, setLoadError] = useState(null);

  const [navBusy, setNavBusy] = useState(false);
  const [submitState, setSubmitState] = useState("idle");
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const refreshServerMedia = useCallback(async (propertyId) => {
    const property = await fetchPropertyDetail(propertyId);
    const { photos, plans } = splitServerImages(property);
    setServerPhotos(photos);
    setServerPlans(plans);
    canonicalMediaRef.current = { image: photos, plan: plans };
    return { photos, plans };
  }, []);

  /**
   * Single orchestration boundary for every is_main PATCH path.
   * Never clears recovery intent until authoritative refresh succeeds.
   */
  const assignServerImageAsMain = useCallback(
    async (propertyId, serverImageId) => {
      try {
        await patchPropertyImage(propertyId, serverImageId, { is_main: true });
      } catch (err) {
        setPendingMainServerAssignment(
          createMainAssignmentIntent(
            propertyId,
            serverImageId,
            MAIN_ASSIGNMENT_STATUS.patch_failed,
            messageFromError(err),
          ),
        );
        throw new MediaWizardRetentionError(
          "Не вдалося призначити головне фото. Спробуйте ще раз.",
        );
      }
      try {
        await refreshServerMedia(propertyId);
        setPendingMainServerAssignment(null);
        setPendingMainLocalId(null);
      } catch (err) {
        setPendingMainServerAssignment(
          createMainAssignmentIntent(
            propertyId,
            serverImageId,
            MAIN_ASSIGNMENT_STATUS.refresh_failed,
            messageFromError(err),
          ),
        );
        throw new MediaWizardRetentionError(
          "Головне фото призначено, але список медіа не оновлено. Спробуйте ще раз.",
        );
      }
    },
    [refreshServerMedia],
  );

  const uploadOne = useCallback(async (id, file, kind) => {
    uploadPropertyIdRef.current = id;
    try {
      return await uploadPropertyImages(id, [file], kind);
    } catch (err) {
      throw new Error(propertyImageUploadErrorMessage(err));
    }
  }, []);

  const onItemUploaded = useCallback(
    async (item, row) => {
      if (item.kind === MEDIA_KIND_PHOTO) {
        setServerPhotos((prev) =>
          sortPropertyImages([...prev.filter((img) => img.id !== row.id), row]),
        );
        canonicalMediaRef.current.image = sortPropertyImages([
          ...(canonicalMediaRef.current.image || []).filter((img) => img.id !== row.id),
          row,
        ]);
        if (pendingMainLocalIdRef.current === item.localId) {
          const propertyId = uploadPropertyIdRef.current || draftIdRef.current;
          if (propertyId) {
            try {
              await assignServerImageAsMain(propertyId, row.id);
            } catch (_err) {
              /* assignment intent is recorded by assignServerImageAsMain */
            }
          }
        }
      } else {
        setServerPlans((prev) =>
          sortPropertyImages([...prev.filter((img) => img.id !== row.id), row]),
        );
        canonicalMediaRef.current.plan = sortPropertyImages([
          ...(canonicalMediaRef.current.plan || []).filter((img) => img.id !== row.id),
          row,
        ]);
      }
    },
    [assignServerImageAsMain],
  );

  const mediaScheduler = usePropertyMediaUploadScheduler({
    propertyId: draftId,
    items: pendingItems,
    mediaQueue,
    uploadOne,
    onItemUploaded,
    enabled: true,
  });

  const applyKindRows = useCallback((kind, rows) => {
    const ordered = sortPropertyImages(rows || []);
    if (kind === MEDIA_KIND_PHOTO) {
      setServerPhotos(ordered);
      canonicalMediaRef.current.image = ordered;
      setPhotosReorderError(null);
    } else {
      setServerPlans(ordered);
      canonicalMediaRef.current.plan = ordered;
      setPlansReorderError(null);
    }
  }, []);

  const mediaReorder = usePropertyMediaReorder({
    propertyId: draftId,
    reorderRequest: reorderPropertyImages,
    onCanonical: (kind, result) => {
      applyKindRows(kind, (result && result.images) || []);
    },
    onFailure: (kind) => {
      if (kind === MEDIA_KIND_PHOTO) {
        setServerPhotos(canonicalMediaRef.current.image || []);
        setPhotosReorderError(REORDER_ERROR_MESSAGE);
      } else {
        setServerPlans(canonicalMediaRef.current.plan || []);
        setPlansReorderError(REORDER_ERROR_MESSAGE);
      }
    },
  });

  const handleReorderPhotos = useCallback((ids) => {
    const nextIds = Array.isArray(ids) ? [...ids] : [];
    const byId = new Map(serverPhotos.map((img) => [img.id, img]));
    setServerPhotos(nextIds.map((id) => byId.get(id)).filter(Boolean));
    mediaReorder.enqueue("image", nextIds);
  }, [mediaReorder, serverPhotos]);

  const handleReorderPlans = useCallback((ids) => {
    const byId = new Map(serverPlans.map((img) => [img.id, img]));
    setServerPlans(ids.map((id) => byId.get(id)).filter(Boolean));
    mediaReorder.enqueue("plan", ids);
  }, [mediaReorder, serverPlans]);

  const drainPendingMedia = useCallback(
    async (propertyId) => {
      if (hasBlockingPrepare(pendingItemsRef.current)) {
        throw new MediaWizardRetentionError(
          "Зачекайте завершення підготовки файлів.",
        );
      }
      await mediaScheduler.drain(propertyId);
      if (hasBlockingMediaAfterDrain(pendingItemsRef.current)) {
        throw new MediaWizardRetentionError(
          pendingItemsRef.current.some(
            (it) => it.phase === "upload_failed" || it.phase === "prepare_failed",
          )
            ? "Виправте помилки медіа перед переходом далі."
            : "Зачекайте завершення завантаження файлів.",
        );
      }
    },
    [mediaScheduler],
  );

  const retryPendingMainAssignment = useCallback(async () => {
    const intent = pendingMainServerAssignmentRef.current;
    if (!intent || !hasPendingMainAssignmentBlocker(intent)) return;
    setMediaUploadBusy(true);
    try {
      if (intent.status === MAIN_ASSIGNMENT_STATUS.patch_failed) {
        await assignServerImageAsMain(intent.propertyId, intent.serverImageId);
        return;
      }

      if (intent.status === MAIN_ASSIGNMENT_STATUS.refresh_failed) {
        try {
          await refreshServerMedia(intent.propertyId);
          setPendingMainServerAssignment(null);
          setPendingMainLocalId(null);
        } catch (err) {
          setPendingMainServerAssignment(
            createMainAssignmentIntent(
              intent.propertyId,
              intent.serverImageId,
              MAIN_ASSIGNMENT_STATUS.refresh_failed,
              messageFromError(err),
            ),
          );
          throw new MediaWizardRetentionError(
            "Головне фото призначено, але список медіа не оновлено. Спробуйте ще раз.",
          );
        }
      }
    } finally {
      setMediaUploadBusy(false);
    }
  }, [assignServerImageAsMain, refreshServerMedia]);

  const setDraftInUrl = useCallback(
    (id) => {
      const next = new URLSearchParams(searchParams);
      next.set(DRAFT_QUERY_PARAM, String(id));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const ensureDraft = useCallback(
    async (currentForm) => {
      if (draftId) return draftId;
      if (draftCreatePromiseRef.current) {
        return draftCreatePromiseRef.current;
      }

      const promise = (async () => {
        const created = await createProperty(
          buildDraftBootstrapPayload(currentForm.core),
        );
        setDraftId(created.id);
        hydratedDraftParamRef.current = created.id;
        setDraftInUrl(created.id);
        return created.id;
      })();

      draftCreatePromiseRef.current = promise;
      try {
        return await promise;
      } finally {
        draftCreatePromiseRef.current = null;
      }
    },
    [draftId, setDraftInUrl],
  );

  const syncFeatures = useCallback(async (propertyId, selected, saved) => {
    const ops = [];
    for (const fid of selected) {
      if (!saved.has(fid)) {
        ops.push(addPropertyFeature(propertyId, fid));
      }
    }
    for (const fid of saved) {
      if (!selected.has(fid)) {
        ops.push(removePropertyFeature(propertyId, fid));
      }
    }
    await Promise.all(ops);
    setSavedFeatureIds(new Set(selected));
  }, []);

  const saveStepProgress = useCallback(
    async (stepNumber, currentForm, currentFeatureIds, propertyId) => {
      if (stepNumber === 2) {
        if (locationReady(currentForm.location, locationCanonicalReady)) {
          await putPropertyLocation(
            propertyId,
            buildLocationPayload(currentForm.location, buildLocationApiPayload),
          );
        }
        return;
      }
      if (stepNumber === 3) {
        const subtype = SUBTYPE_BY_PROPERTY_TYPE[currentForm.core.property_type];
        if (subtypeReady(currentForm.core.property_type, currentForm)) {
          if (subtype === "residential") {
            await putPropertyResidential(
              propertyId,
              buildResidentialPayload(currentForm.residential),
            );
          } else if (subtype === "land") {
            await putPropertyLand(
              propertyId,
              buildLandPayload(currentForm.land),
            );
          } else if (subtype === "commercial") {
            await putPropertyCommercial(
              propertyId,
              buildCommercialPayload(currentForm.commercial),
            );
          }
        }
        return;
      }
      if (stepNumber === 4) {
        await syncFeatures(propertyId, currentFeatureIds, savedFeatureIds);
        return;
      }
      if (stepNumber === 5) {
        if (hasBlockingPrepare(pendingItemsRef.current)) {
          throw new MediaWizardRetentionError(
            "Зачекайте завершення підготовки файлів.",
          );
        }
        await drainPendingMedia(propertyId);
        if (hasBlockingMediaAfterDrain(pendingItemsRef.current)) {
          throw new MediaWizardRetentionError(
            "Виправте помилки медіа перед переходом далі.",
          );
        }
        if (hasPendingMainAssignmentBlocker(pendingMainServerAssignmentRef.current)) {
          throw new MediaWizardRetentionError(
            pendingMainServerAssignmentRef.current.status
              === MAIN_ASSIGNMENT_STATUS.refresh_failed
              ? "Головне фото призначено, але список медіа не оновлено. Спробуйте ще раз."
              : "Не вдалося призначити головне фото. Спробуйте ще раз.",
          );
        }
        if (isFilled(currentForm.core.video_url)) {
          await patchPropertyCore(propertyId, {
            video_url: currentForm.core.video_url,
          });
        }
      }
    },
    [savedFeatureIds, syncFeatures, drainPendingMedia],
  );

  useEffect(() => {
    if (!userInfo) return undefined;
    let cancelled = false;
    setCatalogState("loading");
    fetchFeatureCatalog()
      .then((data) => {
        if (cancelled) return;
        setFeatureCatalog(Array.isArray(data) ? data : []);
        setCatalogState("loaded");
        setCatalogError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setCatalogError(messageFromError(err));
        setCatalogState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [userInfo]);

  useEffect(() => {
    if (!userInfo || !parsedDraftParam) return undefined;
    if (hydratedDraftParamRef.current === parsedDraftParam) return undefined;

    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);

    fetchPropertyDetail(parsedDraftParam)
      .then((property) => {
        if (cancelled) return;
        if (property.status !== "draft") {
          hydratedDraftParamRef.current = parsedDraftParam;
          setLoadState("invalid");
          setLoadError("Це оголошення не є чернеткою.");
          return;
        }
        const hydrated = formFromPropertyDetail(property);
        setForm(hydrated);
        const fids = featureIdsFromProperty(property);
        setSelectedFeatureIds(fids);
        setSavedFeatureIds(new Set(fids));
        const { photos, plans } = splitServerImages(property);
        clearQueue();
        setServerPhotos(photos);
        setServerPlans(plans);
        canonicalMediaRef.current = { image: photos, plan: plans };
        setPendingMainLocalId(null);
        setPendingMainServerAssignment(null);
        setLandParcels(
          Array.isArray(property.land_parcels) ? property.land_parcels : [],
        );
        setDraftId(property.id);
        const resumeStep = resolveResumeStep(property);
        setStep(resumeStep);
        setVisitedSteps(visitedStepsFromResume(resumeStep));
        // Draft resume marks visited steps from the resolved point, but
        // optional steps 4/5 are not auto-completed from position alone.
        setStep4Acknowledged(false);
        setStep5Acknowledged(false);
        setLoadState("ready");
        hydratedDraftParamRef.current = parsedDraftParam;
      })
      .catch((err) => {
        if (cancelled) return;
        hydratedDraftParamRef.current = parsedDraftParam;
        const status = err?.response?.status;
        if (status === 404) {
          setLoadState("not_found");
          setLoadError("Чернетку не знайдено.");
        } else if (status === 403) {
          setLoadState("forbidden");
          setLoadError("Немає доступу до цієї чернетки.");
        } else {
          setLoadState("error");
          setLoadError(messageFromError(err));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userInfo, parsedDraftParam, clearQueue]);

  const goToStep = (n) => {
    if (n < 1 || n > TOTAL_WIZARD_STEPS) return;
    if (n > 1 && !draftId) return;
    setVisitedSteps((prev) => markStepVisited(prev, n));
    setStep(n);
  };

  const handlePrev = () => goToStep(step - 1);

  const handleNext = async () => {
    if (navBusy || step >= TOTAL_WIZARD_STEPS) return;

    if (step === 1) {
      const issues = validateStep1ForDraft(form.core);
      if (Object.keys(issues).length > 0) {
        setFieldErrors(issues);
        setSubmitError("Заповніть обов'язкові поля кроку 1.");
        setVisitedSteps((prev) => markStepVisited(prev, 1));
        return;
      }
      setFieldErrors({});
      setSubmitError(null);
      setNavBusy(true);
      try {
        const ensuredDraftId = await ensureDraft(form);
        if (!ensuredDraftId) {
          throw new Error("Не вдалося створити чернетку.");
        }
        setVisitedSteps((prev) => markStepVisited(markStepVisited(prev, 1), 2));
        setStep(2);
      } catch (err) {
        const fe = mergeCooperationOfferFieldErrors(
          err,
          parseDrfFieldErrors(err, CORE_ERROR_KEYS),
        );
        const errorMessage = mapCreateAsyncError(err, CREATE_DRAFT_ASYNC_FALLBACK);
        setFieldErrors(fe);
        setSubmitError(errorMessage);
        setVisitedSteps((prev) => markStepVisited(prev, 1));
        toast.error(errorMessage);
      } finally {
        setNavBusy(false);
      }
      return;
    }

    if (!draftId) return;

    if (step === 5 && hasBlockingPrepare(pendingItems)) {
      toast.error("Зачекайте завершення підготовки файлів.");
      return;
    }

    setNavBusy(true);
    try {
      await saveStepProgress(step, form, selectedFeatureIds, draftId);
      if (step === 4) {
        setStep4Acknowledged(true);
      }
      if (step === 5) {
        setStep5Acknowledged(true);
      }
      setVisitedSteps((prev) =>
        markStepVisited(markStepVisited(prev, step), step + 1),
      );
      goToStep(step + 1);
    } catch (err) {
      setSubmitError(messageFromError(err));
      toast.error(messageFromError(err));
      setVisitedSteps((prev) => markStepVisited(prev, step));
    } finally {
      setNavBusy(false);
    }
  };

  const handleCoreChange = (next) => {
    setForm((prev) => {
      dismissFieldErrorsForKeys(setFieldErrors, changedKeys(prev.core, next));
      return { ...prev, core: next };
    });
  };
  const handleLocationChange = (next) => {
    setForm((prev) => {
      dismissFieldErrorsForKeys(setFieldErrors, changedKeys(prev.location, next));
      return { ...prev, location: next };
    });
  };
  const handleResidentialChange = (next) => {
    setForm((prev) => {
      dismissFieldErrorsForKeys(
        setFieldErrors,
        changedKeys(prev.residential, next),
      );
      return { ...prev, residential: next };
    });
  };
  const handleLandChange = (next) => {
    setForm((prev) => {
      dismissFieldErrorsForKeys(setFieldErrors, changedKeys(prev.land, next));
      return { ...prev, land: next };
    });
  };
  const handleCommercialChange = (next) => {
    setForm((prev) => {
      dismissFieldErrorsForKeys(
        setFieldErrors,
        changedKeys(prev.commercial, next),
      );
      return { ...prev, commercial: next };
    });
  };

  const handleToggleFeature = (featureId) => {
    setSelectedFeatureIds((prev) => {
      const next = new Set(prev);
      if (next.has(featureId)) next.delete(featureId);
      else next.add(featureId);
      return next;
    });
  };

  const addPhotos = (files) => queueAddFiles(files, MEDIA_KIND_PHOTO);
  const addPlans = (files) => queueAddFiles(files, MEDIA_KIND_PLAN);

  const removePendingPhoto = (localId) => {
    const item = pendingItems.find((it) => it.localId === localId);
    if (item?.kind !== MEDIA_KIND_PHOTO) return;
    if (editingItem?.localId === localId && editingItem?.kind === MEDIA_KIND_PHOTO) {
      setEditingItem(null);
    }
    setPendingMainLocalId((currentMain) =>
      resolvePendingMainAfterRemoval(
        pendingItems.filter((it) => it.localId !== localId),
        localId,
        currentMain,
        Boolean(serverMainId(serverPhotos)),
      ),
    );
    queueRemoveItem(localId);
  };

  const removePendingPlan = (localId) => {
    const item = pendingItems.find((it) => it.localId === localId);
    if (item?.kind !== MEDIA_KIND_PLAN) return;
    if (editingItem?.localId === localId && editingItem?.kind === MEDIA_KIND_PLAN) {
      setEditingItem(null);
    }
    queueRemoveItem(localId);
  };

  const makePendingPhotoMain = (localId) => {
    setPendingMainLocalId(localId);
  };

  const makeServerPhotoMain = async (imageId) => {
    if (!draftId || mediaUploadBusy || navBusy) return;
    setNavBusy(true);
    try {
      await assignServerImageAsMain(draftId, imageId);
      toast.success("Головне зображення оновлено.");
    } catch (err) {
      toast.error(messageFromError(err));
    } finally {
      setNavBusy(false);
    }
  };

  const handleReplaceServer = async (imageId, file) => {
    if (!draftId || mediaUploadBusy || navBusy) return;
    setNavBusy(true);
    try {
      await patchPropertyImage(draftId, imageId, { image: file });
      await refreshServerMedia(draftId);
      setEditingItem(null);
      toast.success("Зображення оновлено.");
    } catch (err) {
      toast.error(messageFromError(err));
    } finally {
      setNavBusy(false);
    }
  };

  const deleteServerImage = async (imageId, kind) => {
    if (!draftId || mediaUploadBusy || navBusy) return;
    setNavBusy(true);
    const intent = pendingMainServerAssignmentRef.current;
    const deletingIntentTarget =
      intent && Number(intent.serverImageId) === Number(imageId);
    try {
      await deletePropertyImage(draftId, imageId);
      try {
        await refreshServerMedia(draftId);
        if (deletingIntentTarget) {
          // Deleted row cannot be re-PATCHed — clear impossible blocker.
          setPendingMainServerAssignment(null);
        }
        toast.success("Зображення видалено.");
      } catch (err) {
        if (deletingIntentTarget) {
          // Keep a refresh-only recoverable intent (retry never PATCHes).
          setPendingMainServerAssignment(
            createMainAssignmentIntent(
              draftId,
              imageId,
              MAIN_ASSIGNMENT_STATUS.refresh_failed,
              messageFromError(err),
            ),
          );
        }
        toast.error(messageFromError(err));
      }
    } catch (err) {
      // Delete failure preserves existing main-assignment intent.
      toast.error(messageFromError(err));
    } finally {
      setNavBusy(false);
    }
  };

  const runCreate = async (publish) => {
    if (submitState === "submitting" || navBusy) return;

    const issues = collectFieldIssues(form);
    if (Object.keys(issues).length > 0) {
      setFieldErrors(issues);
      setSubmitError("Виправте позначені поля перед збереженням.");
      setSubmitState("error");
      const jump = stepForFieldIssues(issues);
      if (jump) setStep(jump);
      return;
    }

    try {
      assertCreateMediaPreflight(
        pendingItemsRef.current,
        pendingMainServerAssignmentRef.current,
      );
    } catch (err) {
      if (isMediaWizardRetentionError(err)) {
        setStep(5);
        setSubmitError(messageFromError(err));
        setSubmitState("error");
        toast.error(messageFromError(err));
        return;
      }
      throw err;
    }

    setSubmitState("submitting");
    setSubmitError(null);
    setFieldErrors({});

    let propertyId = draftId;
    try {
      propertyId = await ensureDraft(form);

      await patchPropertyCore(propertyId, buildCorePayload(form.core));

      if (locationReady(form.location, locationCanonicalReady)) {
        await putPropertyLocation(
          propertyId,
          buildLocationPayload(form.location, buildLocationApiPayload),
        );
      }

      const subtype = SUBTYPE_BY_PROPERTY_TYPE[form.core.property_type];
      if (subtypeReady(form.core.property_type, form)) {
        if (subtype === "residential") {
          await putPropertyResidential(
            propertyId,
            buildResidentialPayload(form.residential),
          );
        } else if (subtype === "land") {
          await putPropertyLand(propertyId, buildLandPayload(form.land));
        } else if (subtype === "commercial") {
          await putPropertyCommercial(
            propertyId,
            buildCommercialPayload(form.commercial),
          );
        }
      }

      await syncFeatures(propertyId, selectedFeatureIds, savedFeatureIds);

      if (hasBlockingPrepare(pendingItemsRef.current)) {
        throw new MediaWizardRetentionError(
          "Зачекайте завершення підготовки файлів.",
        );
      }

      await drainPendingMedia(propertyId);

      if (hasBlockingMediaAfterDrain(pendingItemsRef.current)) {
        throw new MediaWizardRetentionError(
          "Виправте помилки медіа перед збереженням.",
        );
      }

      if (hasPendingMainAssignmentBlocker(pendingMainServerAssignmentRef.current)) {
        throw new MediaWizardRetentionError(
          pendingMainServerAssignmentRef.current.status
            === MAIN_ASSIGNMENT_STATUS.refresh_failed
            ? "Головне фото призначено, але список медіа не оновлено. Спробуйте ще раз."
            : "Не вдалося призначити головне фото. Спробуйте ще раз.",
        );
      }

      if (publish) {
        await publishProperty(propertyId);
      }

      toast.success(
        publish ? "Оголошення опубліковано." : "Чернетку збережено.",
      );
      navigate(`/realty/${propertyId}/edit`, { replace: true });
    } catch (err) {
      console.error("CreateRealtyScreenContent: create failed", err);

      if (isMediaWizardRetentionError(err)) {
        setStep(5);
        setSubmitError(messageFromError(err));
        setSubmitState("error");
        toast.error(messageFromError(err));
        return;
      }

      if (propertyId) {
        toast.info(
          "Оголошення збережено як чернетку. Завершіть заповнення в редакторі.",
        );
        navigate(`/realty/${propertyId}/edit`, { replace: true });
        return;
      }

      const fe = mergeCooperationOfferFieldErrors(
        err,
        parseDrfFieldErrors(err, CORE_ERROR_KEYS),
      );
      const errorMessage = mapCreateAsyncError(
        err,
        publish ? CREATE_PUBLISH_ASYNC_FALLBACK : CREATE_SAVE_ASYNC_FALLBACK,
      );
      setFieldErrors(fe);
      setSubmitError(errorMessage);
      setSubmitState("error");
      toast.error(errorMessage);
      const jump = stepForCoreErrors(fe);
      if (jump) setStep(jump);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
  };

  const submitting = submitState === "submitting";
  const typeLocked = Boolean(draftId);
  const mediaBusy = navBusy || mediaUploadBusy;

  if (loadState === "loading") {
    return (
      <main className="add-property-page" id="Csection">
        <div className="container py-5 text-center">
          <Spinner />
          <p className="mt-3">Завантаження чернетки…</p>
        </div>
      </main>
    );
  }

  if (loadState === "not_found" || loadState === "forbidden" || loadState === "invalid") {
    return (
      <main className="add-property-page" id="Csection">
        <div className="container py-5">
          <EmptyState title="Чернетку не вдалося відкрити" description={loadError} />
          <p className="mt-3">
            <Link to="/add">Почати нове оголошення</Link>
          </p>
        </div>
      </main>
    );
  }

  if (loadState === "error") {
    return (
      <main className="add-property-page" id="Csection">
        <div className="container py-5">
          <EmptyState title="Помилка завантаження" description={loadError} />
        </div>
      </main>
    );
  }

  const stepStates = deriveWizardStepStates({
    currentStep: step,
    visitedSteps,
    form,
    locationCanonicalReadyFn: locationCanonicalReady,
    step4Acknowledged,
    step5Acknowledged,
    selectedFeatureIds,
    savedFeatureIds,
    hasPendingUploads: hasUploadablePending(pendingItems),
  });

  return (
    <main
      className="add-property-page"
      id="Csection"
      tabIndex="-1"
      data-property-type={form.core.property_type}
      data-deal-type={form.core.deal_type}
      data-draft-id={draftId || ""}
    >
      <div className="container">
        <header className="ap-header">
          <Link to="/" className="cd-back">
            <i className="fa-solid fa-arrow-left" aria-hidden="true" />
            На головну
          </Link>
          <div className="ap-header__row">
            <div className="ap-header__text">
              <h1>Нове оголошення</h1>
              <p>
                Заповніть кроки нижче. Оголошення збережеться як{" "}
                <strong>чернетка</strong>; для публікації потрібні тип, локація
                та характеристики.
              </p>
            </div>
            <div className="ap-header__status">
              <span className="status-pill status-pill--lead">
                <span className="dot" />
                Чернетка
              </span>
              <small>Не видно у пошуку</small>
            </div>
          </div>
        </header>

        <CreateStepper
          steps={WIZARD_STEPS}
          current={step}
          stepStates={stepStates}
          onJump={goToStep}
        />

        <form className="ap-form" onSubmit={handleSubmit} noValidate>
          <CreateStepType
            active={step === 1}
            values={form.core}
            onChange={handleCoreChange}
            fieldErrors={fieldErrors}
            typeLocked={typeLocked}
          />
          <CreateStepLocation
            active={step === 2}
            values={form.location}
            onChange={handleLocationChange}
            fieldErrors={fieldErrors}
          />
          <CreateStepSpecs
            active={step === 3}
            propertyType={form.core.property_type}
            residential={form.residential}
            land={form.land}
            commercial={form.commercial}
            fieldErrors={fieldErrors}
            onChangeResidential={handleResidentialChange}
            onChangeLand={handleLandChange}
            onChangeCommercial={handleCommercialChange}
          />
          <div hidden={step !== 3}>
            <LandParcelsEditor
              propertyId={draftId}
              propertyType={form.core.property_type}
              initialParcels={landParcels}
              onParcelsChange={setLandParcels}
              disabled={step !== 3 || navBusy || submitting}
            />
          </div>
          <CreateStepFeatures
            active={step === 4}
            catalog={featureCatalog}
            catalogState={catalogState}
            catalogError={catalogError}
            selectedIds={selectedFeatureIds}
            onToggle={handleToggleFeature}
          />
          <CreateStepMedia
            active={step === 5}
            serverPhotos={
              pendingMainLocalId != null
                ? serverPhotos.map((img) => ({ ...img, is_main: false }))
                : serverPhotos
            }
            serverPlans={serverPlans}
            photos={pendingPhotos}
            plans={pendingPlans}
            onAddPhotos={addPhotos}
            onRemovePhoto={removePendingPhoto}
            onDeleteServerPhoto={(id) =>
              deleteServerImage(id, MEDIA_KIND_PHOTO)
            }
            onMakeServerPhotoMain={makeServerPhotoMain}
            onMakePendingPhotoMain={makePendingPhotoMain}
            pendingMainLocalId={pendingMainLocalId}
            pendingMainServerAssignment={pendingMainServerAssignment}
            onRetryPendingMainAssignment={async () => {
              try {
                await retryPendingMainAssignment();
                toast.success("Головне зображення оновлено.");
              } catch (err) {
                toast.error(messageFromError(err));
              }
            }}
            onAddPlans={addPlans}
            onRemovePlan={removePendingPlan}
            onDeleteServerPlan={(id) => deleteServerImage(id, MEDIA_KIND_PLAN)}
            onReorderPhotos={handleReorderPhotos}
            onReorderPlans={handleReorderPlans}
            photosReorderError={photosReorderError}
            plansReorderError={plansReorderError}
            canReorder={Boolean(draftId)}
            onEditItem={setEditingItem}
            editingItem={editingItem}
            onApplyEdit={(localId, file) => {
              mediaScheduler.forgetAttempts(localId);
              queueApplyEditedFile(localId, file);
              setEditingItem(null);
            }}
            onCancelEdit={() => setEditingItem(null)}
            onReplaceServer={handleReplaceServer}
            onRetryPrepare={(localId) => queueRetryPrepare(localId)}
            onRetryUpload={(localId) => mediaScheduler.retryUpload(localId)}
            videoUrl={form.core.video_url}
            onVideoUrlChange={(value) =>
              handleCoreChange({ ...form.core, video_url: value })
            }
            mediaBusy={mediaBusy}
          />
          {step === 5 && hasUploadablePending(pendingItems) ? (
            <p className="form-hint px-3">
              Файли завантажуються автоматично, щойно чернетка існує. Якщо
              завантаження не вдалося — натисніть «Повторити».
            </p>
          ) : null}
          <CreateStepDescription
            active={step === 6}
            values={form.core}
            onChange={handleCoreChange}
            fieldErrors={fieldErrors}
            onSaveDraft={() => runCreate(false)}
            onPublish={() => runCreate(true)}
            submitting={submitting}
            submitError={submitError}
          />

          <CreateNav
            current={step}
            total={TOTAL_WIZARD_STEPS}
            onPrev={handlePrev}
            onNext={handleNext}
            nextDisabled={navBusy || submitting || hasBlockingPrepare(pendingItems)}
          />
        </form>
      </div>
    </main>
  );
}

export default CreateRealtyScreenContent;
